import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from '@/hooks/use-toast';
import { 
  User, 
  Bell, 
  Shield, 
  Palette, 
  Globe, 
  Truck,
  Database,
  Settings as SettingsIcon,
  Save,
  Camera,
  Mail,
  Phone,
  MapPin,
  Clock,
  Moon,
  Sun
} from 'lucide-react';

const Settings = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [notifications, setNotifications] = useState({
    orders: true,
    system: true,
    email: false,
    sms: true
  });
  const [profile, setProfile] = useState({
    name: 'Nguyễn Văn A',
    email: 'nguyenvana@example.com',
    phone: '0901234567',
    address: 'Hà Nội, Việt Nam',
    role: 'Admin'
  });

  const handleSave = () => {
    toast({
      title: "Đã lưu cài đặt",
      description: "Các thay đổi đã được lưu thành công.",
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-6 w-6" />
          Cài đặt hệ thống
        </h1>
        <p className="text-muted-foreground">
          Cấu hình các thông số hệ thống và tùy chọn cá nhân
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline">Hồ sơ</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Thông báo</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="hidden sm:inline">Bảo mật</span>
          </TabsTrigger>
          <TabsTrigger value="display" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">Hiển thị</span>
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">Hệ thống</span>
          </TabsTrigger>
        </TabsList>

        {/* Profile Settings */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Thông tin cá nhân
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center space-x-4">
                <Avatar className="h-20 w-20">
                  <AvatarImage src="/placeholder.svg" />
                  <AvatarFallback className="text-lg">NA</AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <Button variant="outline" size="sm" className="flex items-center gap-2">
                    <Camera className="h-4 w-4" />
                    Thay đổi ảnh
                  </Button>
                  <Badge variant="secondary">{profile.role}</Badge>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Họ và tên
                  </Label>
                  <Input 
                    id="name" 
                    value={profile.name}
                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </Label>
                  <Input 
                    id="email" 
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({...profile, email: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Số điện thoại
                  </Label>
                  <Input 
                    id="phone" 
                    value={profile.phone}
                    onChange={(e) => setProfile({...profile, phone: e.target.value})}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Địa chỉ
                  </Label>
                  <Input 
                    id="address" 
                    value={profile.address}
                    onChange={(e) => setProfile({...profile, address: e.target.value})}
                  />
                </div>
              </div>

              <Button onClick={handleSave} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                Lưu thông tin
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Cài đặt thông báo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Thông báo đơn hàng</Label>
                    <p className="text-sm text-muted-foreground">
                      Nhận thông báo khi có đơn hàng mới hoặc thay đổi trạng thái
                    </p>
                  </div>
                  <Switch 
                    checked={notifications.orders}
                    onCheckedChange={(checked) => 
                      setNotifications({...notifications, orders: checked})
                    }
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Thông báo hệ thống</Label>
                    <p className="text-sm text-muted-foreground">
                      Nhận thông báo về bảo trì, cập nhật hệ thống
                    </p>
                  </div>
                  <Switch 
                    checked={notifications.system}
                    onCheckedChange={(checked) => 
                      setNotifications({...notifications, system: checked})
                    }
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Thông báo email</Label>
                    <p className="text-sm text-muted-foreground">
                      Gửi thông báo qua email
                    </p>
                  </div>
                  <Switch 
                    checked={notifications.email}
                    onCheckedChange={(checked) => 
                      setNotifications({...notifications, email: checked})
                    }
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Thông báo SMS</Label>
                    <p className="text-sm text-muted-foreground">
                      Gửi thông báo qua tin nhắn SMS
                    </p>
                  </div>
                  <Switch 
                    checked={notifications.sms}
                    onCheckedChange={(checked) => 
                      setNotifications({...notifications, sms: checked})
                    }
                  />
                </div>
              </div>

              <Button onClick={handleSave} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                Lưu cài đặt
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Cài đặt bảo mật
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Mật khẩu hiện tại</Label>
                  <Input id="current-password" type="password" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new-password">Mật khẩu mới</Label>
                  <Input id="new-password" type="password" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Xác nhận mật khẩu mới</Label>
                  <Input id="confirm-password" type="password" />
                </div>

                <Button variant="outline" className="w-full">
                  Đổi mật khẩu
                </Button>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="font-medium">Xác thực hai yếu tố</h3>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label>Bật xác thực 2FA</Label>
                    <p className="text-sm text-muted-foreground">
                      Tăng cường bảo mật cho tài khoản
                    </p>
                  </div>
                  <Switch />
                </div>

                <Button variant="outline">
                  Thiết lập xác thực 2FA
                </Button>
              </div>

              <Separator />

              <div className="space-y-4">
                <h3 className="font-medium">Phiên đăng nhập</h3>
                <p className="text-sm text-muted-foreground">
                  Quản lý các phiên đăng nhập trên thiết bị khác
                </p>
                <Button variant="destructive" size="sm">
                  Đăng xuất tất cả thiết bị
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Display Settings */}
        <TabsContent value="display">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Cài đặt hiển thị
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="flex items-center gap-2">
                      {isDarkMode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                      Chế độ tối
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Chuyển đổi giữa chế độ sáng và tối
                    </p>
                  </div>
                  <Switch 
                    checked={isDarkMode}
                    onCheckedChange={setIsDarkMode}
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Ngôn ngữ hiển thị</Label>
                  <Select defaultValue="vi">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vi">Tiếng Việt</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Múi giờ</Label>
                  <Select defaultValue="asia-ho-chi-minh">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asia-ho-chi-minh">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          GMT+7 (Hồ Chí Minh)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Số dòng hiển thị mỗi trang</Label>
                  <Select defaultValue="20">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 dòng</SelectItem>
                      <SelectItem value="20">20 dòng</SelectItem>
                      <SelectItem value="50">50 dòng</SelectItem>
                      <SelectItem value="100">100 dòng</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button onClick={handleSave} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                Lưu cài đặt
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Settings */}
        <TabsContent value="system">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Cài đặt hệ thống
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Tự động đối soát</Label>
                  <Select defaultValue="weekly">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Hàng ngày</SelectItem>
                      <SelectItem value="weekly">Hàng tuần</SelectItem>
                      <SelectItem value="monthly">Hàng tháng</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tự động sao lưu dữ liệu</Label>
                  <div className="flex items-center gap-2">
                    <Switch defaultChecked />
                    <span className="text-sm text-muted-foreground">Mỗi 6 giờ</span>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Cảnh báo hết hàng</Label>
                  <Input type="number" placeholder="Số lượng tối thiểu" defaultValue="10" />
                </div>

                <div className="space-y-2">
                  <Label>Thời gian tự động khóa phiên (phút)</Label>
                  <Input type="number" placeholder="30" defaultValue="30" />
                </div>

                <Separator />

                <div className="space-y-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    Tích hợp 3PL
                  </h3>
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Tự động chuyển đơn sang 3PL</Label>
                      <p className="text-sm text-muted-foreground">
                        Tự động chuyển đơn hàng khi vượt quá khoảng cách
                      </p>
                    </div>
                    <Switch />
                  </div>

                  <div className="space-y-2">
                    <Label>Khoảng cách tối đa (km)</Label>
                    <Input type="number" placeholder="100" defaultValue="100" />
                  </div>
                </div>
              </div>

              <Button onClick={handleSave} className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                Lưu cài đặt
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings; 